import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Info, Loader2 } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { OrganizationCompanySaveActions } from '@/features/organization-settings/components/organization-company-save-actions'
import type { InstallationGoodsFamily } from '@/features/installations/installation-create.schema'
import { SupplierInstallationAddressSectionCard } from '@/features/suppliers/components/supplier-installation-address-section-card'
import { SupplierInstallationGeneralSectionCard } from '@/features/suppliers/components/supplier-installation-general-section-card'
import {
  useSupplierInstallationMutations,
  useSupplierInstallationQuery,
} from '@/features/suppliers/hooks/use-supplier-installations'
import { useSupplierOrganizationQuery } from '@/features/suppliers/hooks/use-supplier-organizations'
import {
  buildSupplierInstallationFormState,
  buildSupplierInstallationUpdatePayload,
  SUPPLIER_INSTALLATION_LOCAL_INPUT_TO_STATE,
  type SupplierInstallationFormState,
} from '@/features/suppliers/lib/supplier-installation-form-state'
import {
  isSupplierInstallationNameTooLong,
  isSupplierInstallationNameValid,
} from '@/features/suppliers/lib/supplier-installation-name'
import { useDoesOrganizationRequiresLocalAddress } from '@/shared/hooks/api/organizations'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { useSession } from '@/shared/hooks/useSession'
import { SupplierInstallationEmissionReportsSectionCard } from '@/features/suppliers/components/supplier-installation-emission-reports-section-card'

export function SupplierInstallationEditPage() {
  const { t } = useTranslation(['installations', 'settings', 'common'])
  const { t: tCountryName } = useTranslation('countriesByIsoAlpha2')
  const { installationId, supplierOrganizationId, supplierInstallationId } = useParams<{
    installationId: string
    supplierOrganizationId: string
    supplierInstallationId: string
  }>()

  const { session } = useSession()
  const { checkPermission } = usePermissions()
  const canEdit = checkPermission('organization', 'update')
  const installationQuery = useSupplierInstallationQuery(
    supplierOrganizationId,
    supplierInstallationId
  )
  const supplierOrgQuery = useSupplierOrganizationQuery(undefined, supplierOrganizationId)
  const { updateSupplierInstallation } = useSupplierInstallationMutations(
    supplierOrganizationId
  )

  const [state, setState] = useState<SupplierInstallationFormState | null>(null)
  const [isFormPristine, setIsFormPristine] = useState(true)

  useEffect(() => {
    if (installationQuery.data) {
      const initial = buildSupplierInstallationFormState(installationQuery.data)
      if (!initial.country.trim()) {
        initial.country =
          supplierOrgQuery.data?.country ??
          session.organization.country ??
          session.organization.placeOfRegistration ??
          ''
      }
      setState(initial)
      setIsFormPristine(true)
    }
  }, [
    installationQuery.data,
    supplierOrgQuery.data?.country,
    session.organization.country,
    session.organization.placeOfRegistration,
  ])

  const doesRequireLocalAddress = useDoesOrganizationRequiresLocalAddress(state?.country)
  const latinErrorMessage = t('common:errors.latinCharacters')
  const markDirty = useCallback(() => setIsFormPristine(false), [])

  const onGeneralChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = evt.target
      setState((prev) => (prev ? { ...prev, [id]: value } : prev))
      markDirty()
    },
    [markDirty]
  )

  const onGoodsFamilyChange = useCallback(
    (value: InstallationGoodsFamily | '') => {
      setState((prev) => (prev ? { ...prev, goodsFamily: value } : prev))
      markDirty()
    },
    [markDirty]
  )

  const onSingleCountryAddressInput = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = evt.target
      setState((prev) => {
        if (!prev) {
          return prev
        }
        return {
          ...prev,
          [id]: value,
          localStreet: id === 'street' ? value : prev.localStreet,
          localStreetNumber: id === 'streetNumber' ? value : prev.localStreetNumber,
          localPoBox: id === 'poBox' ? value : prev.localPoBox,
          localCity: id === 'city' ? value : prev.localCity,
          localPostCode: id === 'postCode' ? value : prev.localPostCode,
          localStreet2: id === 'street2' ? value : prev.localStreet2,
        }
      })
      markDirty()
    },
    [markDirty]
  )

  const onCnEnglishAddressInput = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = evt.target
      setState((prev) => (prev ? { ...prev, [id]: value } : prev))
      markDirty()
    },
    [markDirty]
  )

  const onCnLocalAddressInput = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = evt.target
      const baseId = id.startsWith('local-') ? id.replace(/^local-/, '') : id
      const key = SUPPLIER_INSTALLATION_LOCAL_INPUT_TO_STATE[baseId]
      if (!key) {
        return
      }
      setState((prev) => (prev ? { ...prev, [key]: value } : prev))
      markDirty()
    },
    [markDirty]
  )

  const onLocationFieldChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = evt.target
      setState((prev) => (prev ? { ...prev, [id]: value } : prev))
      markDirty()
    },
    [markDirty]
  )

  const onSaveChanges = useCallback(async () => {
    if (!state || !supplierInstallationId) {
      return
    }
    try {
      await updateSupplierInstallation.mutateAsync({
        supplierInstallationId,
        payload: buildSupplierInstallationUpdatePayload(state),
      })
      toast.success(
        t('installations:suppliers.edit.installations.edit.notifications.saveSuccess')
      )
      setIsFormPristine(true)
    } catch {
      toast.error(
        t('installations:suppliers.edit.installations.edit.notifications.saveError')
      )
    }
  }, [state, supplierInstallationId, updateSupplierInstallation, t])

  const nameTooLong = state ? isSupplierInstallationNameTooLong(state.name) : false
  const isNameValid = state ? isSupplierInstallationNameValid(state.name) : false
  const nameError = nameTooLong
    ? t('installations:suppliers.edit.installations.nameMaxLengthError')
    : undefined
  const isSaveDisabled = !isNameValid || isFormPristine
  const saveDisabledHint =
    isFormPristine || isNameValid
      ? undefined
      : t('installations:suppliers.edit.installations.edit.saveDisabledHint.name')

  const countryCodeLower = (state?.country ?? '').trim().toLowerCase()
  const countryName =
    countryCodeLower.length === 2 ? tCountryName(countryCodeLower) : (state?.country ?? '')

  const supplierOrgEditPath = `/dashboard/${installationId}/suppliers/${supplierOrganizationId}/home`

  if (installationQuery.isLoading || !state) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (installationQuery.isError) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-6">
        <p className="text-muted-foreground">
          {t('installations:suppliers.edit.installations.edit.loadError')}
        </p>
        <Button type="button" variant="outline" asChild>
          <Link to={supplierOrgEditPath}>
            <ArrowLeft className="size-4" />
            {t('installations:suppliers.edit.installations.edit.backToSupplier')}
          </Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6">
      <div className="flex flex-col gap-4">
        <Button type="button" variant="ghost" className="w-fit px-0" asChild>
          <Link to={supplierOrgEditPath}>
            <ArrowLeft className="size-4" />
            {t('installations:suppliers.edit.installations.edit.backToSupplier')}
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('installations:suppliers.edit.installations.edit.title', { name: state.name })}
          </h1>
          <p className="text-muted-foreground">
            {t('installations:suppliers.edit.installations.edit.description')}
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <Alert>
          <Info className="size-4" />
          <AlertDescription>
            {t('installations:suppliers.edit.installations.edit.alert')}
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-medium">
              {t('installations:suppliers.edit.installations.edit.general.title')}
            </h3>
            <Separator className="my-4" />
            <SupplierInstallationGeneralSectionCard
              state={state}
              doesRequireLocalName={doesRequireLocalAddress}
              countryCode={state.country}
              nameError={nameError}
              variant="plain"
              onChange={onGeneralChange}
              onGoodsFamilyChange={onGoodsFamilyChange}
            />
          </div>

          <div>
            <h3 className="text-lg font-medium">
              {t('settings:organizationDetails.details.address')}
            </h3>
            <Separator className="my-4" />
            <SupplierInstallationAddressSectionCard
              state={state}
              countryName={countryName}
              doesRequireLocalAddress={doesRequireLocalAddress}
              latinErrorMessage={latinErrorMessage}
              variant="plain"
              onSingleCountryAddressInput={onSingleCountryAddressInput}
              onCnEnglishAddressInput={onCnEnglishAddressInput}
              onCnLocalAddressInput={onCnLocalAddressInput}
              onLocationFieldChange={onLocationFieldChange}
            />
          </div>

          {supplierOrganizationId && supplierInstallationId ? (
            <div>
              <div>
                <h3 className="text-lg font-medium">
                  {t(
                    'installations:suppliers.edit.installations.edit.emissionReports.title'
                  )}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'installations:suppliers.edit.installations.edit.emissionReports.description'
                  )}
                </p>
              </div>
              <Separator className="my-4" />
              <SupplierInstallationEmissionReportsSectionCard
                supplierOrganizationId={supplierOrganizationId}
                supplierInstallationId={supplierInstallationId}
                canEdit={canEdit}
                variant="plain"
              />
            </div>
          ) : null}
        </div>

        <OrganizationCompanySaveActions
          saveLabel={t('common:actions.save', { ns: 'common' })}
          isSaving={updateSupplierInstallation.isPending}
          isSaveDisabled={isSaveDisabled}
          saveDisabledHint={saveDisabledHint}
          onSave={onSaveChanges}
        />
      </div>
    </div>
  )
}
